import * as z from "zod";
//#region src/runtime/client-config.ts
let apiBaseUrl = "";
let authTokenGetter = () => void 0;
/** Базовый URL MDM. Вызвать один раз на старте приложения. */
const setApiBaseUrl = (url) => {
	apiBaseUrl = url.replace(/\/$/, "");
};
const getApiBaseUrl = () => apiBaseUrl;
/**
* Источник Bearer-токена. В eacd — `() => auth.user?.access_token` из OIDC-контекста.
* Мутатор зовёт getter на каждый запрос, поэтому ротация токена подхватывается без переинициализации.
*/
const setAuthTokenGetter = (getter) => {
	authTokenGetter = getter;
};
const getAuthToken = () => authTokenGetter();
//#endregion
//#region src/runtime/gge-gateway-path.ts
const isGgeRuntime = () => import.meta.env?.VITE_SYSTEM_TYPE === "GGE";
const startsWithAny = (url, prefixes) => prefixes.some((prefix) => url === prefix || url.startsWith(`${prefix}/`));
const GGE_AUTH_PATH_PREFIXES = ["/auth", "/v2/auth"];
const GGE_PROJECT_PATH_PREFIXES = [
	"/gantt",
	"/meta",
	"/project",
	"/projects",
	"/roles"
];
const GGE_FILES_PATH_PREFIXES = [
	"/directory",
	"/document",
	"/pdf",
	"/collision-document",
	"/model/repository",
	"/knowledge-base-document",
	"/report-generation-cache"
];
/** Prepends GGE API gateway segments for relative OpenAPI paths at runtime. */
const withGgeGatewayPath = (url) => {
	if (!isGgeRuntime() || !url) return url;
	if (url.startsWith("/api/")) return url;
	if (startsWithAny(url, GGE_AUTH_PATH_PREFIXES)) return `/api/auth${url}`;
	if (startsWithAny(url, GGE_PROJECT_PATH_PREFIXES)) return `/api/project${url}`;
	if (startsWithAny(url, GGE_FILES_PATH_PREFIXES)) return `/api/files${url}`;
	return url;
};
//#endregion
//#region src/runtime/validate.ts
z.config(z.locales.ru());
const getValueByPath = (source, path) => {
	let current = source;
	for (const segment of path) {
		const key = typeof segment === "object" ? segment.key : segment;
		if (current === null || current === void 0 || typeof current !== "object") return;
		current = current[key];
	}
	return current;
};
const formatIssuePath = (path) => {
	if (path.length === 0) return "корень ответа";
	return path.reduce((acc, key) => {
		if (typeof key === "number") return acc === "" ? `[${key}]` : `${acc}[${key}]`;
		return acc === "" ? String(key) : `${acc}.${String(key)}`;
	}, "");
};
/** Детальный RU-лог расхождения ответа со схемой (по полям + сводка). */
const logZodError = (context, error) => {
	const pretty = z.prettifyError(error);
	const byField = error.issues.map((issue) => {
		const field = formatIssuePath(issue.path);
		const received = "input" in issue && issue.input !== void 0 ? `\n     полученное значение: ${JSON.stringify(issue.input)}` : "";
		return `  • Поле «${field}»\n    ${issue.message}${received}`;
	}).join("\n");
	console.error([
		"════════════════════════════════════════════════════════════",
		"Ошибка валидации ответа внешнего API",
		"════════════════════════════════════════════════════════════",
		`Контекст: ${context}`,
		"",
		"Сводка:",
		pretty,
		"",
		"Детализация по полям:",
		byField,
		"════════════════════════════════════════════════════════════"
	].join("\n"));
};
/** Валидирует `data` схемой `schema`; при расхождении логирует детально и бросает ZodError. */
const parseApiResponse = (schema, data, context) => {
	const parsed = schema.safeParse(data, { reportInput: true });
	if (!parsed.success) {
		logZodError(context, parsed.error);
		throw parsed.error;
	}
	return parsed.data;
};
/**
* SOFT-валидация ответа: при расхождении со схемой логирует детально (с HTTP-методом и путём),
* но НЕ бросает — возвращает фактические данные как есть. Запрос успешен → UI работает,
* расхождение контракта видно в консоли. Вставляется в сгенерированный client (см. codegen post-process).
*/
const softParse = (schema, data, context) => {
	const parsed = schema.safeParse(data, { reportInput: true });
	if (!parsed.success) {
		logZodError(context, parsed.error);
		return data;
	}
	return parsed.data;
};
/**
* SOFT-валидация ответа для сгенерированного Kubb v5 клиента (`.kubb/client.ts`).
* Новый рантайм валидирует ответ через StandardSchema-интерфейс (`validator.response`) и бросает
* ParseError при расхождении со схемой (см. `.kubb/standardSchema.ts`). Здесь то же самое, но
* НЕ бросаем: логируем подробно (с HTTP-методом и путём) и возвращаем фактические данные как есть.
* Запрос успешен → UI работает, расхождение контракта видно в консоли.
* Аналог `softParse` для нового рантайма; встраивается в `.kubb/client.ts` (см. codegen post-process).
*/
const softParseStandard = async (validator, data, context) => {
	const std = validator;
	if (!std?.["~standard"]) return data;
	const result = await std["~standard"].validate(data);
	const issues = "issues" in result ? result.issues : void 0;
	if (!issues) return "value" in result ? result.value : data;
	const byField = issues.map((issue) => {
		const path = issue.path ?? [];
		const field = path.map((p) => typeof p === "object" ? String(p.key) : String(p)).join(".");
		const receivedValue = getValueByPath(data, path);
		const received = receivedValue === void 0 ? "" : `\n    полученное значение: ${JSON.stringify(receivedValue)}`;
		return `  • Поле «${field || "корень ответа"}»\n    ${issue.message ?? "значение не соответствует схеме"}${received}`;
	}).join("\n");
	console.error([
		"════════════════════════════════════════════════════════════",
		"Расхождение ответа API со схемой (soft-валидация)",
		"════════════════════════════════════════════════════════════",
		`Контекст: ${context}`,
		"",
		"Детализация по полям:",
		byField,
		"════════════════════════════════════════════════════════════"
	].join("\n"));
	return data;
};
/**
* Обработчик ошибки для react-query (QueryCache/MutationCache `onError`).
* Сгенерированный Kubb-клиент валидирует ответ через `schema.parse()` и бросает ZodError —
* этот хелпер ловит именно ZodError и даёт тот же детальный RU-лог, что и parseApiResponse.
* Не-ZodError пропускает (их обрабатывает обычная error-логика приложения).
*/
const handleZodError = (error, context) => {
	if (error instanceof z.ZodError) logZodError(context, error);
};
/**
* Композитор для пагинированных списков: springdoc отдаёт `PageResponse.content` как `unknown[]`
* (generic Page не типизирован) — точечно подставляем схему элемента критичного списка.
* Пример: `pageOf(getRequestResponse)` валидирует элементы как полноценную заявку.
*/
const pageOf = (item) => z.object({
	content: z.array(item).optional(),
	page: z.number().optional(),
	size: z.number().optional(),
	totalElements: z.number().optional(),
	totalPages: z.number().optional()
});
//#endregion
export { softParse as a, getApiBaseUrl as c, setAuthTokenGetter as d, parseApiResponse as i, getAuthToken as l, logZodError as n, softParseStandard as o, pageOf as r, withGgeGatewayPath as s, handleZodError as t, setApiBaseUrl as u };

//# sourceMappingURL=validate.js.map